import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import BasicDatatable from "../Common/BasicDatatable";
import {
  getColumns, initialSort
} from "./OrgManagementtableConfig";
import IntegrationService from "../../services/integrations/Integrations";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { RELATIONSHIP_TYPE_OPTIONS } from "../../utils/utils";
import { DateRangeValue, Options, OrgManagementRow, RelationshipType } from "../../utils/types";
import { GlobalContext } from "../../context/GlobalContext";

const normalizeRelationshipType = (value?: string | null): RelationshipType | "-" => {
  if (!value) {
    return "-";
  }

  const normalizedValue = value.trim().toUpperCase() as RelationshipType;

  return RELATIONSHIP_TYPE_OPTIONS.includes(normalizedValue) ? normalizedValue : "-";
};

const mapRows = (data: any[] = []): OrgManagementRow[] =>
  data.map((item: any) => ({
    id: item.orgId,
    domain: item.domain || item.orgDomain || "-",
    type: normalizeRelationshipType(
      item.relationshipTypeFromAccountSrc || item.relationshipType || item.type
    ),
    accountName:
      item.accountName || item.accountDisplayName || item.clientName || item.orgName || "-",
    mrr: item.mrr ?? "-",
    subOrgId: item.subOrgId,
    startDate: item.onboardedDate || item.startDate,
    endDate: item.offboardedDate || item.endDate,
    accountManagerName: item.accountManagerName,
    accountManagerEmail: item.accountManager || item.accountManagerEmail,
    clientType: item.clientType,
  }));

type OrgManagementProps = {
  domainFilter?: string;
  onDomainFilterChange?: (value: string) => void;
  hasActiveDomainFilter?: boolean;
  accountNameFilter?: string;
  onAccountNameFilterChange?: (value: string) => void;
  relationshipTypeFilter?: string;
  setRelationshipTypeFilter?: (value: string) => void;
  accountManagerFilter?: string[];
  setAccountManagerFilter?: (value: string[]) => void;
  onboardedDateRange?: DateRangeValue;
  setOnboardedDateRange?: (value?: DateRangeValue) => void;
  offboardedDateRange?: DateRangeValue;
  setOffboardedDateRange?: (value?: DateRangeValue) => void;
  activeUsers: any[];
  clientTypeOptions: Options[];
  clientType?: string[];
  setClientType?: (value: string[]) => void;
};

const OrgManagement = ({
  domainFilter = "",
  onDomainFilterChange,
  hasActiveDomainFilter = false,
  accountNameFilter = "",
  onAccountNameFilterChange,
  relationshipTypeFilter = "",
  setRelationshipTypeFilter,
  accountManagerFilter = [],
  setAccountManagerFilter,
  onboardedDateRange,
  setOnboardedDateRange,
  offboardedDateRange,
  setOffboardedDateRange,
  activeUsers,
  clientTypeOptions,
  clientType = [],
  setClientType,
}: OrgManagementProps) => {
  const queryClient = useQueryClient();
  const { accountDetails } = useContext(GlobalContext);
  const { showSnackbar } = useDisplaySnackbar();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showSpinner, setShowSpinner] = useState(false);
  const [rows, setRows] = useState<OrgManagementRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});
  const [cellErrors, setCellErrors] = useState<Record<string, boolean>>({});
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [sort, setSort] = useState<{
    sort_field: string;
    sort_order: "asc" | "desc";
    }>({
    sort_field: "accountName",
    sort_order: "asc",
  });
  const isAdmin = ["admin", "superaccess"].includes(accountDetails?.roleId?.toLowerCase() || "");

  useEffect(() => {
    setPageNumber(1);
  }, [
    domainFilter,
    accountNameFilter,
    relationshipTypeFilter,
    accountManagerFilter,
    clientType,
    onboardedDateRange?.startDate,
    onboardedDateRange?.endDate,
    offboardedDateRange?.startDate,
    offboardedDateRange?.endDate,
  ]);

  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data,
  } = IntegrationService.useGetOrgManagement(
    {
      offset: pageNumber,
      limit: pageSize,
      sortOrder: sort.sort_order,
      sortBy: sort.sort_field,
      searchDomain: domainFilter.trim(),
      accountName: accountNameFilter.trim(),
      relationshipType: relationshipTypeFilter,
      ...(accountManagerFilter.length
        ? { accountManagers: accountManagerFilter }
        : {}),
      ...(onboardedDateRange?.startDate
        ? { onboardedStartDate: onboardedDateRange.startDate }
        : {}),
      ...(onboardedDateRange?.endDate
        ? { onboardedEndDate: onboardedDateRange.endDate }
        : {}),
      ...(offboardedDateRange?.startDate
        ? { offboardedStartDate: offboardedDateRange.startDate }
        : {}),
      ...(offboardedDateRange?.endDate
        ? { offboardedEndDate: offboardedDateRange.endDate }
        : {}),
      ...(clientType.length ? { clientType: clientType } : {}),
    },
    true
  );

  const { mutateAsync: updateDomainManagement } = IntegrationService.useUpdateOrgManagement({
    onMutate: () => {
      setShowSpinner(true);
    },
    onSettled: () => {
      setShowSpinner(false);
    },
  });

  useEffect(() => {
    setShowSpinner(isLoading || isFetching);

    // Avoid applying stale cached data while a refetch is in progress
    if (isSuccess && data && !isFetching) {
      const result = data?.data || data || [];
      const mappedRows = mapRows(Array.isArray(result) ? result : []);
      setRows(mappedRows);
      setRowCount(data?.totalRecords ?? mappedRows.length);
    }

    if (isError) {
      showSnackbar("error", error?.message || error || "Failed to load data.");
      setRows([]);
      setRowCount(0);
    }
  }, [isLoading, isFetching, isSuccess, isError, data, error]);

  const getCellKey = (rowId: string, field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType") => `${rowId}-${field}`;

  const getIsSaving = (rowId: string, field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType") =>
    Boolean(savingCells[getCellKey(rowId, field)]);

  const getHasError = (rowId: string, field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType") =>
    Boolean(cellErrors[getCellKey(rowId, field)]);

  const handleInlineSave = async (
    row: OrgManagementRow,
    field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType",
    value: string
  ) => {
    const cellKey = getCellKey(row.id, field);
    const trimmedValue = value.trim();
    const normalizedRelationshipType = normalizeRelationshipType(trimmedValue);
    const clearFieldName =
      field === "type"
        ? "relationshipType"
        : field === "accountName"
        ? "accountName"
        : field === "startDate"
        ? "onboardedDate"
        : field === "endDate"
        ? "offboardedDate"
        : field === "clientType"
        ? "clientType"
        : "mrr";
    const shouldClearField =
      (field === "accountName" && trimmedValue === "") ||
      (field === "type" && trimmedValue === "") ||
      (field === "startDate" && trimmedValue === "") ||
      (field ===  "endDate" && trimmedValue === "") ||
      (field === "clientType" && trimmedValue === "");

    const currentRowType = normalizeRelationshipType(row.type);
    const currentAccountName = (row.accountName ?? "").toString().trim();
    const currentMrr = row.mrr === "-" || row.mrr === null || row.mrr === undefined
      ? ""
      : String(row.mrr).trim();
    const currentStartDate = row.startDate ?? "";
    const currentEndDate = row.endDate ?? "";
    const currentClientType = row.clientType ?? "";

    const isUnchanged = 
      (field === "type" && normalizedRelationshipType === currentRowType && !shouldClearField) ||
      (field === "accountName" && trimmedValue === currentAccountName && !shouldClearField) ||
      (field === "mrr" && trimmedValue === currentMrr && !shouldClearField) ||
      (field === "startDate" && trimmedValue === currentStartDate) ||
      (field === "endDate" && trimmedValue === currentEndDate) ||
      (field === "clientType" && trimmedValue === currentClientType);

    if (isUnchanged) {
      setCellErrors((prev) => ({
        ...prev,
        [cellKey]: false,
      }));
      return;
    }

    const hasError =
      (field === "type" &&
        trimmedValue !== "" &&
        (normalizedRelationshipType === "-" ||
          !RELATIONSHIP_TYPE_OPTIONS.includes(normalizedRelationshipType as RelationshipType))) ||
      (field === "accountName" && false) ||
      (field === "mrr" &&
        trimmedValue.length > 0 &&
        (Number.isNaN(Number(trimmedValue)) || Number(trimmedValue) < 0));

    setCellErrors((prev) => ({
      ...prev,
      [cellKey]: hasError,
    }));

    if (hasError) {
      return;
    }

    setSavingCells((prev) => ({
      ...prev,
      [cellKey]: true,
    }));

    let previousRowsSnapshot: OrgManagementRow[] = [];
    try {
      const payload: {
        orgId: string;
        relationshipType?: RelationshipType;
        accountName?: string;
        mrr?: number;
        subOrgId: string;
        fieldsToClear?: string[];
        onboardedDate?: string;
        offboardedDate?: string;
        clientType?: string;
      } = { orgId: row.id, subOrgId: row.subOrgId };

      if (shouldClearField) {
        payload.fieldsToClear = [clearFieldName];
      } else if (field === "type") {
        payload.relationshipType = normalizedRelationshipType as RelationshipType;
      }

      if (field === "accountName" && !shouldClearField) {
        payload.accountName = trimmedValue;
      }

      if (field === "mrr" && trimmedValue.length > 0) {
        payload.mrr = Number(trimmedValue);
      }

      if (field === "startDate" && !shouldClearField) {
        payload.onboardedDate = value;
      }
      if (field === "endDate" && !shouldClearField) {
        payload.offboardedDate = value;
      }
      if (field === "clientType" && !shouldClearField) {
        payload.clientType = value;
      }
      // Optimistically update UI so user sees only the latest value.
      setRows((prev) => {
        previousRowsSnapshot = prev;
        return prev.map((currentRow) =>
          currentRow.id === row.id
            ? {
                ...currentRow,
              ...(field === "type" ? { type: normalizedRelationshipType } : {}),
                ...(field === "accountName"
                  ? { accountName: shouldClearField ? "" : trimmedValue }
                  : {}),
                ...(field === "mrr"
                  ? { mrr: trimmedValue.length > 0 ? Number(trimmedValue) : currentRow.mrr }
                  : {}),
                ...(field === "startDate"
                  ? { startDate: shouldClearField ? "" : trimmedValue }
                  : {}),
                ...(field === "endDate"
                  ? { endDate: shouldClearField ? "" : trimmedValue }
                  : {}),
                ...(field === "clientType" ? { clientType: shouldClearField ? "" : trimmedValue } : {}),
              }
            : currentRow
        );
      });

      await updateDomainManagement(payload);
      setCellErrors((prev) => ({
        ...prev,
        [cellKey]: false,
      }));
      showSnackbar("success", "Changes updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["get_org_management"],
      });
    } catch (mutationError: any) {
      // Roll back optimistic update on failure.
      if (previousRowsSnapshot.length) {
        setRows(previousRowsSnapshot);
      }
      setCellErrors((prev) => ({
        ...prev,
        [cellKey]: true,
      }));
      showSnackbar(
        "error",
        mutationError?.message || mutationError?.data?.message
      );
    } finally {
      setSavingCells((prev) => ({
        ...prev,
        [cellKey]: false,
      }));
    }
  };

  const handleClearRelationshipType = async (row: OrgManagementRow) => {
    const cellKey = getCellKey(row.id, "type");
    const currentRowType = normalizeRelationshipType(row.type);

    if (currentRowType === "-") {
      return;
    }

    setSavingCells((prev) => ({
      ...prev,
      [cellKey]: true,
    }));

    let previousRowsSnapshot: OrgManagementRow[] = [];
    try {
      // Optimistically clear relationship type in UI.
      setRows((prev) => {
        previousRowsSnapshot = prev;
        return prev.map((currentRow) =>
          currentRow.id === row.id ? { ...currentRow, type: "-" } : currentRow
        );
      });

      await updateDomainManagement({
        orgId: row.id,
        subOrgId: row.subOrgId,
        fieldsToClear: ["relationshipType"],
      });

      setCellErrors((prev) => ({
        ...prev,
        [cellKey]: false,
      }));
      showSnackbar("success", "Relationship type cleared successfully");
      queryClient.invalidateQueries({
        queryKey: ["get_org_management"],
      });
    } catch (mutationError: any) {
      if (previousRowsSnapshot.length) {
        setRows(previousRowsSnapshot);
      }
      setCellErrors((prev) => ({
        ...prev,
        [cellKey]: true,
      }));
      showSnackbar(
        "error",
        mutationError?.message || mutationError?.data?.message
      );
    } finally {
      setSavingCells((prev) => ({
        ...prev,
        [cellKey]: false,
      }));
    }
  };

  const handleAccountManagerUpdate = async (
    row: OrgManagementRow,
    email: string | null
  ) => {
    const currentAccountManagerEmail = (row.accountManagerEmail ?? "").trim();
    const nextAccountManagerEmail = (email ?? "").trim();

    if (currentAccountManagerEmail === nextAccountManagerEmail) {
      return;
    }

    let previousRowsSnapshot: OrgManagementRow[] = [];

    try {
      setRows((prev) => {
        previousRowsSnapshot = prev;
        return prev.map((currentRow) =>
          currentRow.id === row.id
            ? {
                ...currentRow,
                accountManagerEmail: nextAccountManagerEmail || undefined,
                accountManagerName: nextAccountManagerEmail
                  ? activeUsers.find((user: any) => user.email === nextAccountManagerEmail)?.name ||
                    row.accountManagerName ||
                    undefined
                  : undefined,
              }
            : currentRow
        );
      });

      await updateDomainManagement({
        orgId: row.id,
        subOrgId: row.subOrgId,
        ...(nextAccountManagerEmail
          ? { accountManager: nextAccountManagerEmail }
          : { fieldsToClear: ["accountManager"] }),
      });

      queryClient.invalidateQueries({
        queryKey: ["get_org_management"],
      });

      showSnackbar("success", "Account manager updated successfully");
    } catch (mutationError: any) {
      if (previousRowsSnapshot.length) {
        setRows(previousRowsSnapshot);
      }
      showSnackbar(
        "error",
        mutationError?.message || mutationError?.data?.message
      );
    }
  };

  const columns = getColumns({
    onSave: handleInlineSave,
    getIsSaving,
    getHasError,
    sort,
    domainFilter,
    setDomainFilter: (value: string) => {
      onDomainFilterChange?.(value);
      setPageNumber(1);
    },
    accountNameFilter,
    setAccountNameFilter: (value: string) => {
      onAccountNameFilterChange?.(value);
      setPageNumber(1);
    },
    accountManagerFilter,
    setAccountManagerFilter: (value: string[]) => {
      setAccountManagerFilter?.(value);
      setPageNumber(1);
    },
    onboardedDateRange,
    setOnboardedDateRange: (value?: DateRangeValue) => {
      setOnboardedDateRange?.(value);
      setPageNumber(1);
    },
    offboardedDateRange,
    setOffboardedDateRange: (value?: DateRangeValue) => {
      setOffboardedDateRange?.(value);
      setPageNumber(1);
    },
    relationshipTypeFilter,
    setRelationshipTypeFilter: (value: string) => {
      setRelationshipTypeFilter?.(value);
      setPageNumber(1);
    },
    onSortChange: (field: string, order: "asc" | "desc") => {
      setSort({ sort_field: field,sort_order: order });
      setPageNumber(1);
    },
    openFilterColumn, 
    setOpenFilterColumn,
    onClearRelationshipType: handleClearRelationshipType,
    activeUsers,
    handleAccountManagerUpdate,
    isAdmin,
    clientTypeOptions,
    clientType,
    setClientType,
  });
  const tableHeight = hasActiveDomainFilter
    ? "calc(100vh - 290px)"
    : "calc(100vh - 260px)";

  return (
    <Box
      sx={{
        height: hasActiveDomainFilter ? "calc(100vh - 220px)" : "calc(100vh - 190px)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <BasicDatatable
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        isBordervisible={true}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPageNumber(1);
        }}
        onPaginationChange={(newPage) => setPageNumber(newPage + 1)}
        showSpinner={showSpinner}
        hidePaginationFooter={rowCount <= 1}
        minHeight="calc(10vh)"
        height={tableHeight}
        maxHeight={tableHeight}
        initialSort={initialSort}
      />
    </Box>
  );
};

export default OrgManagement;
